import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Alert,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined as PrizeIcon,
  ScheduleOutlined as ScheduleIcon,
  CasinoOutlined as LotteryIcon,
  SportsEsportsOutlined as MineIcon,
  AutoAwesomeOutlined as MRLIcon,
  RocketLaunchOutlined as MissileIcon,
  CasinoOutlined as SlotIcon,
  InfoOutlined as InfoIcon,
  MonetizationOnOutlined as CostIcon,
} from "@mui/icons-material";
import BigNumber from "bignumber.js";
import web3 from "../../../Service/web3";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { lotteryTypeMapper, DEFAULT_CURRENCY } from "../../../../static/Constants";
import {
  CommonTextField,
  CommonDateTimePicker,
  CommonButton,
  CommonChip,
} from "../../../Components/ui";
import {
  POKIES_TYPE_OPTIONS,
  getSymbolsByCategory,
} from "./slotSymbolConfig";

export const CONTRACT_INDEX_COLORS = [
  { num: 0, label: "CONTRACT #0", bg: "rgba(239, 68, 68, 0.08)", color: "#dc2626", border: "#fca5a5" },
  { num: 1, label: "CONTRACT #1", bg: "rgba(37, 99, 235, 0.08)", color: "#2563eb", border: "#93c5fd" },
  { num: 2, label: "CONTRACT #2", bg: "rgba(16, 185, 129, 0.08)", color: "#059669", border: "#6ee7b7" },
  { num: 3, label: "CONTRACT #3", bg: "rgba(139, 92, 246, 0.08)", color: "#7c3aed", border: "#c4b5fd" },
  { num: 4, label: "CONTRACT #4", bg: "rgba(245, 158, 11, 0.08)", color: "#d97706", border: "#fcd34d" },
  { num: 5, label: "CONTRACT #5", bg: "rgba(6, 182, 212, 0.08)", color: "#0891b2", border: "#67e8f9" },
  { num: 6, label: "CONTRACT #6", bg: "rgba(236, 72, 153, 0.08)", color: "#db2777", border: "#f472b6" },
];

export const GAME_TYPE_CONFIG = {
  "0": {
    id: "0",
    label: lotteryTypeMapper[0] || "SpinBET",
    tagline: "Single-Player Roulette",
    icon: <LotteryIcon sx={{ fontSize: 18, color: colors.primary.main }} />,
    color: colors.primary.main,
    desc: "Single-player: Prize rolls over automatically if nobody wins.",
  },
  "1": {
    id: "1",
    label: lotteryTypeMapper[1] || "MineBET",
    tagline: "Sold-Out Instant Draw",
    icon: <MineIcon sx={{ fontSize: 18, color: colors.emerald[600] }} />,
    color: colors.emerald[600],
    desc: "Mine: Results drawn automatically after all tickets sell out.",
  },
  "2": {
    id: "2",
    label: lotteryTypeMapper[2] || "MegaBET",
    tagline: "Multi-Round Progressive",
    icon: <MRLIcon sx={{ fontSize: 18, color: colors.amber[600] }} />,
    color: colors.amber[600],
    desc: "Multi-round: Winners announced when ticket target met and date reached.",
  },
  "3": {
    id: "3",
    label: lotteryTypeMapper[3] || "RocketBET",
    tagline: "Progressive Missile",
    icon: <MissileIcon sx={{ fontSize: 18, color: colors.red[600] }} />,
    color: colors.red[600],
    desc: "Missile: Progressive game where each rollover increases the level by one.",
  },
  "4": {
    id: "4",
    label: lotteryTypeMapper[4] || "3-SlotBET",
    tagline: "3-Reel Classic Pokies",
    icon: <SlotIcon sx={{ fontSize: 18, color: colors.purple[600] }} />,
    color: colors.purple[600],
    desc: "3-Slot: Classic 3-reel progressive pokies with single center payline, 5 contract symbols, and 3-tier jackpot pools.",
  },
  "5": {
    id: "5",
    label: lotteryTypeMapper[5] || "5-SlotBET",
    tagline: "5-Reel Vegas Pokies",
    icon: <SlotIcon sx={{ fontSize: 18, color: colors.blue[600] }} />,
    color: colors.blue[600],
    desc: "5-Slot: 5-reel progressive pokies with center payline, 7 contract symbols, and 3-tier jackpot pools.",
  },
};

const safeFromWei = (val) => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, "ether");
  } catch (e) {
    return String(val);
  }
};

const CreateLotteryForm = ({
  // Standard Lottery Props
  lotterytype,
  setlotterytype,
  partnerlist = [],
  selectPartner,
  setSelectPartner,
  entryfee,
  setentryfee,
  totalPrize,
  settotalPrize,
  totalnumber,
  settotalnumber,
  capacity,
  setcapacity,
  unlockCode,
  setUnlockCode,
  startTime,
  setstartTime,
  endtime,
  setendtime,
  setdrawdate,
  Rollover,
  setRollover,
  Rolloverday,
  setRolloverdays,
  partnerPercent,
  setPartnerPercent,
  minPlayers,
  setminPlayers,
  lotteryCreateFee = "5",
  approved,
  validated,
  loading,
  currencySymbol = DEFAULT_CURRENCY || "POL",
  onSubmit,

  // 3-Slot Specific Props
  machineName = "AutoBET PNG Kina 3-Slot",
  setMachineName,
  slotCategory = "fiat",
  setSlotCategory,
  slotSymbols = [],
  setSlotSymbols,
  onCategoryChange,
  theme = "fiat",
  setTheme,
  currency = "PGK",
  setCurrency,
  betAmount = "1.00",
  setBetAmount,
  seedJackpot = "100.00",
  setSeedJackpot,
  progressivePercent = "3.5",
  setProgressivePercent,
  miniPercent = "50",
  setMiniPercent,
  majorPercent = "30",
  setMajorPercent,
  megaPercent = "20",
  setMegaPercent,
  maxSpins = "0",
  setMaxSpins,
  slotStartTime,
  setSlotStartTime,
  slotEndTime,
  setSlotEndTime,
  slotPartner = "",
  setSlotPartner,
  slotPartnerPercent = "0",
  setSlotPartnerPercent,
}) => {
  const isThreeSlot = String(lotterytype) === "4" || String(lotterytype) === "threeslot";
  const isFiveSlot = String(lotterytype) === "5" || String(lotterytype) === "fiveslot";
  const isSlot = isThreeSlot || isFiveSlot;
  const slotReelCount = isFiveSlot ? 5 : 3;
  const symbolCount = isFiveSlot ? 7 : 5;

  // Active symbols for slot mapped to contract numbers (5 for 3-slot, 7 for 5-slot)
  const activeSlotSymbols = useMemo(() => {
    if (slotSymbols && slotSymbols.length === symbolCount) {
      return slotSymbols;
    }
    return getSymbolsByCategory(slotCategory, symbolCount);
  }, [slotSymbols, slotCategory, symbolCount]);

  // Standard prize limit validation
  const minPrizeWei = approved?.minPrize ? Number(approved.minPrize) : 0;
  const maxPrizeWei = approved?.maxPrize ? Number(approved.maxPrize) : 0;
  const hasPrizeBounds = minPrizeWei > 0 && maxPrizeWei > 0;

  let isStandardPrizeInBounds = true;
  if (totalPrize !== "" && hasPrizeBounds && web3?.utils?.toWei) {
    try {
      const prizeWei = Number(web3.utils.toWei(`${totalPrize}`, "ether"));
      isStandardPrizeInBounds = prizeWei >= minPrizeWei && prizeWei <= maxPrizeWei;
    } catch {
      isStandardPrizeInBounds = false;
    }
  }

  const standardCreationFeeTotal = totalPrize
    ? (Number(totalPrize) * Number(lotteryCreateFee || 0)) / 100 + Number(totalPrize)
    : 0;

  // Slot calculations
  const numericSeed = Number(seedJackpot || 0);
  const numericBet = Number(betAmount || 0);
  const numericFeePercent = Number(lotteryCreateFee || 0);
  const slotProtocolFeeAmount = (numericSeed * numericFeePercent) / 100;
  const totalSlotRequiredDeposit = numericSeed + slotProtocolFeeAmount;

  const miniSeedVal = (numericSeed * (Number(miniPercent) || 50)) / 100;
  const majorSeedVal = (numericSeed * (Number(majorPercent) || 30)) / 100;
  const megaSeedVal = (numericSeed * (Number(megaPercent) || 20)) / 100;

  let isSlotPrizeInBounds = true;
  if (seedJackpot !== "" && hasPrizeBounds && web3?.utils?.toWei) {
    try {
      const prizeWei = Number(web3.utils.toWei(`${seedJackpot}`, "ether"));
      isSlotPrizeInBounds = prizeWei >= minPrizeWei && prizeWei <= maxPrizeWei;
    } catch {
      isSlotPrizeInBounds = true;
    }
  }

  const tierSum = (Number(miniPercent) || 0) + (Number(majorPercent) || 0) + (Number(megaPercent) || 0);
  const isTierSumValid = tierSum === 100;

  const currentGame = GAME_TYPE_CONFIG[String(lotterytype)] || GAME_TYPE_CONFIG["0"];

  return (
    <Box component="form" noValidate onSubmit={onSubmit} sx={{ width: "100%" }}>
      {/* ========================================================================= */}
      {/* Section 1: Game Type & Partner Selection */}
      {/* ========================================================================= */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: borderRadiusPx.md,
              bgcolor: "rgba(37, 99, 235, 0.1)",
              color: colors.primary.main,
            }}
          >
            <LotteryIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: colors.slate[900],
              fontSize: "16px",
              letterSpacing: "-0.01em",
            }}
          >
            {isSlot ? "Game Type" : "Game Type & Partner"}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={!isSlot ? 6 : 12}>
            <CommonTextField
              select
              label="Lottery Type"
              value={String(lotterytype)}
              onChange={(event) => {
                setlotterytype(event.target.value);
                if (settotalnumber) settotalnumber("1");
              }}
            >
              {Object.entries(lotteryTypeMapper).map(([typeId, typeName]) => (
                <MenuItem key={typeId} value={String(typeId)}>
                  {typeName}
                </MenuItem>
              ))}
            </CommonTextField>
          </Grid>

          {!isSlot && (
            <Grid item xs={12} sm={6}>
              <CommonTextField
                select
                label="Select Partner"
                value={selectPartner}
                onChange={(e) => setSelectPartner(e.target.value)}
                disabled={partnerlist.length === 0}
                error={Boolean(validated && partnerlist.length > 0 && selectPartner === "")}
                helperText={
                  validated && partnerlist.length > 0 && selectPartner === ""
                    ? "Please select a partner"
                    : partnerlist.length === 0
                    ? "No partners available"
                    : undefined
                }
              >
                {partnerlist.length === 0 ? (
                  <MenuItem value="" disabled>
                    No partners available
                  </MenuItem>
                ) : (
                  [
                    <MenuItem key="default-empty" value="" disabled>
                      Select Partner
                    </MenuItem>,
                    ...partnerlist.map((item) => (
                      <MenuItem key={item.partnerAddress} value={item.partnerAddress}>
                        {item.name} ({item.partnerAddress.slice(0, 6)}...{item.partnerAddress.slice(-4)})
                      </MenuItem>
                    )),
                  ]
                )}
              </CommonTextField>
            </Grid>
          )}
        </Grid>

        {/* Dynamic Mode Information Box */}
        <Box
          sx={{
            mt: 1.5,
            py: 1,
            px: 1.5,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.dashboard.cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            {currentGame.icon}
          </Box>
          <Typography sx={{ fontSize: "12.5px", color: colors.slate[600], lineHeight: 1.4 }}>
            <strong style={{ color: colors.slate[800] }}>{currentGame.label}:</strong>{" "}
            {currentGame.desc}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

      {/* ========================================================================= */}
      {/* SLOT PROGRESSIVE POKIES CONFIGURATION FORM (when lotterytype is 4 or 5)  */}
      {/* ========================================================================= */}
      {isSlot ? (
        <>
          {/* Section 2 (Slot): Pokies Symbol Edition (5 or 7 Symbols) */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: "rgba(139, 92, 246, 0.1)",
                  color: colors.purple[600],
                }}
              >
                <SlotIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    fontSize: "16px",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {`2. Pokies Symbol Edition (${symbolCount} Symbols)`}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: colors.slate[500] }}>
                  {`Select Fiat or Crypto pokies edition (${symbolCount} contract-mapped symbols).`}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {/* Single Dropdown with 2 Options: Fiat vs Crypto */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  select
                  label="Pokies Symbol Edition"
                  value={slotCategory}
                  onChange={(e) => {
                    if (onCategoryChange) {
                      onCategoryChange(e.target.value);
                    } else if (setSlotCategory) {
                      setSlotCategory(e.target.value);
                    }
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 280,
                        },
                      },
                    },
                  }}
                >
                  {POKIES_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </CommonTextField>
              </Grid>

              {/* Game Currency */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Game Currency"
                  value="BET (BET Token)"
                  disabled
                  helperText="ERC-20 BET token used for slot bets, instant payouts & progressive jackpots"
                />
              </Grid>

              {/* Interactive Symbol Preview Cards */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    borderRadius: borderRadiusPx.xl,
                    bgcolor: colors.slate[50],
                    border: `1px solid ${colors.slate[200]}`,
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 800, color: colors.slate[900] }}>
                      {`Active ${symbolCount}-Symbol Pokies Set (${isFiveSlot ? "5-SlotBET" : "3-SlotBET"})`}
                    </Typography>
                    <Typography sx={{ fontSize: "11.5px", color: colors.slate[500], mt: 0.25 }}>
                      {isFiveSlot
                        ? "Winning outcomes: Mini Jackpot (0), Major Jackpot (1), Mega Jackpot (2), Free Spins (3), Super Spins (4), Big Win (5), and Super Wild (6)."
                        : "Winning outcomes: Mini Jackpot (0), Major Jackpot (1), Mega Jackpot (2), 5 Free Spins (3), and 10 Super Free Spins (4)."}
                    </Typography>
                  </Box>

                  {/* Symbol Mapping Grid */}
                  <div className={`symbol-mapping-grid symbol-mapping-grid-${symbolCount}`}>
                    {activeSlotSymbols.map((sym, index) => {
                      const colorInfo = CONTRACT_INDEX_COLORS[index] || CONTRACT_INDEX_COLORS[0];
                      return (
                        <div key={sym.id || index} className="symbol-mapping-card">
                          <Box
                            className="symbol-contract-badge"
                            sx={{
                              bgcolor: colorInfo.bg,
                              color: colorInfo.color,
                              border: `1px solid ${colorInfo.border}`,
                            }}
                          >
                            #{index}
                          </Box>

                          <div className="symbol-img-container">
                            {sym.image ? (
                              <img
                                src={sym.image}
                                alt={sym.label}
                                className="symbol-img-preview"
                              />
                            ) : (
                              <Typography className="symbol-fallback-icon">
                                {sym.iconText || sym.symbolText || "🪙"}
                              </Typography>
                            )}
                          </div>

                          <Typography className="symbol-name-text" title={sym.label}>
                            {sym.label}
                          </Typography>

                          <Box
                            className="symbol-multiplier-badge"
                            sx={{
                              bgcolor: `${sym.tierColor || "#f59e0b"}18`,
                              color: sym.tierColor || "#f59e0b",
                              border: `1px solid ${sym.tierColor || "#f59e0b"}40`,
                              mt: 0.75,
                              mb: 0,
                            }}
                          >
                            {sym.payout || `${sym.multiplier}x Payout`}
                          </Box>
                        </div>
                      );
                    })}
                  </div>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

          {/* Section 3 (3-Slot): Bet Pricing & Progressive Jackpot Pools */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: "rgba(16, 185, 129, 0.1)",
                  color: colors.emerald[600],
                }}
              >
                <PrizeIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    fontSize: "16px",
                    letterSpacing: "-0.01em",
                  }}
                >
                  3. Bet Pricing & 3-Tier Progressive Jackpot Pools
                </Typography>
                <Typography sx={{ fontSize: "12px", color: colors.slate[500] }}>
                  Configure single spin entry ticket and seed initial guaranteed liquidity
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {/* Bet Amount (Min Bet) */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Minimum Bet per Spin (Min Bet)"
                  type="number"
                  placeholder="1.00"
                  value={betAmount}
                  endAdornment={currency}
                  onChange={(e) => setBetAmount && setBetAmount(e.target.value)}
                  error={Boolean(
                    validated &&
                      (betAmount === "" ||
                        numericBet <= 0 ||
                        (seedJackpot !== "" && numericBet > numericSeed))
                  )}
                  helperText={
                    validated && numericBet <= 0
                      ? "Min bet must be greater than 0"
                      : validated && seedJackpot !== "" && numericBet > numericSeed
                      ? "Min bet cannot exceed maximum bet"
                      : `Minimum allowable bet for 1 spin (${currency})`
                  }
                />
              </Grid>

              {/* Max Bet / Seed Jackpot */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Maximum Bet per Spin (Max Bet)"
                  type="number"
                  placeholder="100.00"
                  value={seedJackpot}
                  endAdornment={currency}
                  onChange={(e) => setSeedJackpot && setSeedJackpot(e.target.value)}
                  error={Boolean(validated && (seedJackpot === "" || numericSeed <= 0 || !isSlotPrizeInBounds))}
                  helperText={
                    validated && numericSeed <= 0
                      ? "Enter valid maximum bet amount"
                      : validated && !isSlotPrizeInBounds && hasPrizeBounds
                      ? `Must be between ${safeFromWei(approved?.minPrize)} and ${safeFromWei(approved?.maxPrize)} ${currency}`
                      : `Maximum allowable bet per spin (${currency})`
                  }
                />
              </Grid>

              {/* Progressive Bet Contribution % */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Progressive Contribution Rate"
                  type="number"
                  placeholder="3.5"
                  value={progressivePercent}
                  endAdornment="%"
                  onChange={(e) => setProgressivePercent && setProgressivePercent(e.target.value)}
                  error={Boolean(
                    validated &&
                      (progressivePercent === "" ||
                        Number(progressivePercent) < 1 ||
                        Number(progressivePercent) > 10)
                  )}
                  helperText={
                    validated && (Number(progressivePercent) < 1 || Number(progressivePercent) > 10)
                      ? "Recommended progressive rate is 1% – 10% (Default: 3.5%)"
                      : "% of each player's bet added to jackpot pools"
                  }
                />
              </Grid>

              {/* Max Spins / Capacity */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Max Spins / Capacity (Optional)"
                  type="number"
                  placeholder="0 (Unlimited)"
                  value={maxSpins}
                  onChange={(e) => setMaxSpins && setMaxSpins(e.target.value)}
                  helperText="Set 0 for continuous unlimited progressive machine"
                />
              </Grid>

              {/* 3-Tier Jackpot Pool Distribution */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: borderRadiusPx.lg,
                    bgcolor: colors.slate[50],
                    border: `1px solid ${colors.slate[200]}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.slate[800] }}>
                        3-Tier Progressive Pool Seed Distribution
                      </Typography>
                      <Typography sx={{ fontSize: "11.5px", color: colors.slate[500] }}>
                        Split initial seed and incoming progressive bets across Mini, Major & Mega pools
                      </Typography>
                    </Box>
                    <CommonChip
                      label={`Total: ${tierSum}%`}
                      variant={isTierSumValid ? "success" : "error"}
                      size="small"
                    />
                  </Stack>

                  <Grid container spacing={2}>
                    {/* Mini */}
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.5, borderRadius: borderRadiusPx.md, bgcolor: "#ffffff", border: "1px solid #e2e8f0" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#10b981" }}>
                            🟢 Mini Jackpot (0: Min Bet)
                          </Typography>
                          <Typography sx={{ fontSize: "12px", fontWeight: 800, color: colors.slate[800] }}>
                            {miniPercent}%
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: "11px", color: colors.slate[500], my: 0.5 }}>
                          Seed: <strong>{miniSeedVal.toFixed(2)} {currency}</strong>
                        </Typography>
                        <CommonTextField
                          size="small"
                          type="number"
                          value={miniPercent}
                          endAdornment="%"
                          onChange={(e) => setMiniPercent && setMiniPercent(e.target.value)}
                        />
                      </Box>
                    </Grid>

                    {/* Major */}
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.5, borderRadius: borderRadiusPx.md, bgcolor: "#ffffff", border: "1px solid #e2e8f0" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#2563eb" }}>
                            🔵 Major Jackpot (1: Major)
                          </Typography>
                          <Typography sx={{ fontSize: "12px", fontWeight: 800, color: colors.slate[800] }}>
                            {majorPercent}%
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: "11px", color: colors.slate[500], my: 0.5 }}>
                          Seed: <strong>{majorSeedVal.toFixed(2)} {currency}</strong>
                        </Typography>
                        <CommonTextField
                          size="small"
                          type="number"
                          value={majorPercent}
                          endAdornment="%"
                          onChange={(e) => setMajorPercent && setMajorPercent(e.target.value)}
                        />
                      </Box>
                    </Grid>

                    {/* Mega */}
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.5, borderRadius: borderRadiusPx.md, bgcolor: "#ffffff", border: "1px solid #e2e8f0" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#ef4444" }}>
                            🔴 Mega Jackpot (2: Mega)
                          </Typography>
                          <Typography sx={{ fontSize: "12px", fontWeight: 800, color: colors.slate[800] }}>
                            {megaPercent}%
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: "11px", color: colors.slate[500], my: 0.5 }}>
                          Seed: <strong>{megaSeedVal.toFixed(2)} {currency}</strong>
                        </Typography>
                        <CommonTextField
                          size="small"
                          type="number"
                          value={megaPercent}
                          endAdornment="%"
                          onChange={(e) => setMegaPercent && setMegaPercent(e.target.value)}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {!isTierSumValid && (
                    <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, fontSize: "12px" }}>
                      The sum of Mini, Major, and Mega percentages must equal 100% (Current: {tierSum}%).
                    </Alert>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

          {/* Section 4 (3-Slot): Schedule & Co-Host Configuration */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: "rgba(245, 158, 11, 0.1)",
                  color: colors.amber[600],
                }}
              >
                <ScheduleIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    fontSize: "16px",
                    letterSpacing: "-0.01em",
                  }}
                >
                  4. Schedule & Co-Host Configuration
                </Typography>
                <Typography sx={{ fontSize: "12px", color: colors.slate[500] }}>
                  Timing parameters and optional partner revenue share
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <CommonDateTimePicker
                  label="Game Launch Date & Time"
                  value={slotStartTime || startTime}
                  onChange={(value) => setSlotStartTime ? setSlotStartTime(value) : setstartTime(value)}
                  format="dd-MM-yy h:mma"
                />
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={6}>
                <CommonDateTimePicker
                  label="End / Settlement Date & Time"
                  value={slotEndTime || endtime}
                  onChange={(value) => setSlotEndTime ? setSlotEndTime(value) : setendtime(value)}
                  format="dd-MM-yy h:mma"
                />
              </Grid>

              {/* Select Partner */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  select
                  label="Select Partner Co-Host (Optional)"
                  value={slotPartner || selectPartner}
                  onChange={(e) => setSlotPartner ? setSlotPartner(e.target.value) : setSelectPartner(e.target.value)}
                  disabled={partnerlist.length === 0}
                >
                  <MenuItem value="">No Partner Co-Host (Sole Creator)</MenuItem>
                  {partnerlist.map((item) => (
                    <MenuItem key={item.partnerAddress} value={item.partnerAddress}>
                      {item.name} ({item.partnerAddress.slice(0, 6)}...{item.partnerAddress.slice(-4)})
                    </MenuItem>
                  ))}
                </CommonTextField>
              </Grid>

              {/* Partner Share % */}
              {(slotPartner || selectPartner) !== "" && (
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Partner's Margin Share %"
                    type="number"
                    placeholder="0 - 100"
                    value={slotPartnerPercent || partnerPercent}
                    endAdornment="%"
                    onChange={(e) => setSlotPartnerPercent ? setSlotPartnerPercent(e.target.value) : setPartnerPercent(e.target.value)}
                    error={Boolean(
                      validated &&
                        (parseFloat(slotPartnerPercent || partnerPercent) < 0 ||
                          parseFloat(slotPartnerPercent || partnerPercent) > 100)
                    )}
                    helperText="Percentage of creator's net platform margin shared with co-host"
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Section 5 (3-Slot): Cost Calculation Breakdown & Deployment Action */}
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: borderRadiusPx.xl,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.dashboard.cardBorder}`,
              boxShadow: customShadows.cardFlat,
              mt: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: borderRadiusPx.lg,
                    bgcolor: colors.blue[50],
                    color: colors.primary.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CostIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: colors.slate[500],
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Total Required Initial Deposit
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "22px", sm: "26px" },
                      fontWeight: 900,
                      color: colors.slate[900],
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {totalSlotRequiredDeposit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })} {currency}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography sx={{ fontSize: "12.5px", color: colors.slate[600] }}>
                  Guaranteed Seed ({numericSeed} {currency}) + {lotteryCreateFee}% Protocol Fee ({slotProtocolFeeAmount.toFixed(4)} {currency})
                </Typography>
                <Typography sx={{ fontSize: "12px", color: colors.emerald[700], fontWeight: 600, mt: 0.25 }}>
                  Entry Spin: <strong>{numericBet} {currency}</strong> • Progressive Rate: <strong>{progressivePercent}%</strong>
                </Typography>
              </Box>
            </Box>

            {/* Prize bounds warning */}
            {seedJackpot !== "" && hasPrizeBounds && !isSlotPrizeInBounds && (
              <Alert
                severity="error"
                icon={<InfoIcon />}
                sx={{
                  mb: 2,
                  py: 0.5,
                  borderRadius: borderRadiusPx.md,
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                Initial seed must be between {safeFromWei(approved?.minPrize)} and {safeFromWei(approved?.maxPrize)} {currency}
              </Alert>
            )}

            {!isTierSumValid && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  py: 0.5,
                  borderRadius: borderRadiusPx.md,
                  fontSize: "12px",
                }}
              >
                Tier distribution must equal 100% (Current: {tierSum}%)
              </Alert>
            )}

            {/* Submit Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
              <CommonButton
                type="submit"
                variant="contained"
                size="large"
                loading={loading}
                disabled={loading || !isTierSumValid || (seedJackpot !== "" && hasPrizeBounds && !isSlotPrizeInBounds)}
                sx={{
                  minWidth: { xs: "100%", sm: "280px" },
                  py: 1.35,
                  fontWeight: 800,
                  fontSize: "15px",
                  letterSpacing: "0.02em",
                  boxShadow: customShadows.primaryGlow,
                }}
              >
                {loading
                  ? `Deploying ${slotReelCount}-SlotBET...`
                  : `Deploy ${slotReelCount}-SlotBET`}
              </CommonButton>
            </Box>
          </Box>
        </>
      ) : (
        /* ========================================================================= */
        /* STANDARD LOTTERY CONFIGURATION FORM (when lotterytype is 0, 1, 2, or 3)  */
        /* ========================================================================= */
        <>
          {/* Section 2: Ticket Pricing & Prize Pool */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: "rgba(16, 185, 129, 0.1)",
                  color: colors.emerald[600],
                }}
              >
                <PrizeIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: colors.slate[900],
                  fontSize: "16px",
                  letterSpacing: "-0.01em",
                }}
              >
                2. Ticket Pricing & Prize Pool
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {/* Ticket Fee */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Ticket Fee"
                  type="number"
                  placeholder="0.00"
                  value={entryfee}
                  endAdornment={currencySymbol}
                  onChange={(event) => {
                    const val = event.target.value;
                    setentryfee(val);
                    const ep = Number(val);
                    if (totalPrize !== "" && ep > 0) {
                      try {
                        const calculated = BigNumber(
                          BigNumber(totalPrize).dividedBy(ep)
                        )
                          .plus(
                            BigNumber(
                              BigNumber(
                                BigNumber(totalPrize).multipliedBy(10)
                              ).dividedBy(ep)
                            ).dividedBy(100)
                          )
                          ?.c?.[0];
                        if (setminPlayers) setminPlayers(calculated ? String(calculated) : "0");
                      } catch {}
                    }
                  }}
                  error={
                    Boolean(
                      validated &&
                        (entryfee === "" ||
                          Number(entryfee) < 0 ||
                          (totalPrize !== "" && Number(entryfee) >= parseFloat(totalPrize)))
                    )
                  }
                  helperText={
                    validated && Number(entryfee) < 0
                      ? "Ticket fee cannot be negative"
                      : validated &&
                        totalPrize !== "" &&
                        Number(entryfee) >= parseFloat(totalPrize)
                      ? "Must be less than prize amount"
                      : validated && entryfee === ""
                      ? "Please enter ticket fee"
                      : undefined
                  }
                />
              </Grid>

              {/* Winner Prize Amount */}
              <Grid item xs={12} sm={6}>
                <CommonTextField
                  label="Winner Prize Amount"
                  type="number"
                  placeholder="0.00"
                  value={totalPrize}
                  endAdornment={currencySymbol}
                  onChange={(event) => {
                    const val = event.target.value;
                    settotalPrize(val);
                    const ep = Number(val);
                    if (entryfee !== "" && Number(entryfee) > 0) {
                      try {
                        const calculated = BigNumber(
                          BigNumber(ep).dividedBy(entryfee)
                        )
                          .plus(
                            BigNumber(
                              BigNumber(
                                BigNumber(ep).multipliedBy(10)
                              ).dividedBy(entryfee)
                            ).dividedBy(100)
                          )
                          ?.c?.[0];
                        if (setminPlayers) setminPlayers(calculated ? String(calculated) : "0");
                      } catch {}
                    }
                  }}
                  error={
                    Boolean(
                      validated &&
                        (totalPrize === "" ||
                          Number(totalPrize) < 0 ||
                          !isStandardPrizeInBounds)
                    )
                  }
                  helperText={
                    validated && Number(totalPrize) < 0
                      ? "Prize amount cannot be negative"
                      : validated && totalPrize === ""
                      ? "Please enter prize amount"
                      : validated && !isStandardPrizeInBounds && hasPrizeBounds
                      ? `Prize must be ${safeFromWei(approved?.minPrize)} - ${safeFromWei(approved?.maxPrize)} ${currencySymbol}`
                      : undefined
                  }
                />
              </Grid>

              {/* Conditional: Numbers to be selected & Lottery Size OR Unlock Code Length */}
              {lotterytype !== "3" ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <CommonTextField
                      label="Numbers to be selected"
                      type="number"
                      placeholder="Enter Number"
                      value={totalnumber}
                      disabled={lotterytype === "0" || lotterytype === "1"}
                      onChange={(event) => settotalnumber(event.target.value)}
                      error={
                        Boolean(
                          validated &&
                            (Number(totalnumber) < 1 ||
                              (capacity !== "" &&
                                Number(totalnumber) >= parseFloat(capacity)))
                        )
                      }
                      helperText={
                        validated && Number(totalnumber) < 1
                          ? "Must be at least 1"
                          : validated &&
                            capacity !== "" &&
                            Number(totalnumber) >= parseFloat(capacity)
                          ? "Must be less than lottery size"
                          : lotterytype === "0" || lotterytype === "1"
                          ? "Fixed to 1 for this game type"
                          : undefined
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <CommonTextField
                      label="Lottery size"
                      type="number"
                      placeholder="Enter total Numbers"
                      value={capacity}
                      onChange={(event) => setcapacity(event.target.value)}
                      error={
                        Boolean(
                          validated &&
                            (capacity === "" ||
                              ((lotterytype === "0" || lotterytype === "2") &&
                                parseInt(capacity, 10) < 5) ||
                              parseInt(capacity, 10) < 3)
                        )
                      }
                      helperText={
                        validated && capacity === ""
                          ? "Please enter lottery size"
                          : validated &&
                            (lotterytype === "0" || lotterytype === "2") &&
                            parseInt(capacity, 10) < 5
                          ? "Size must be at least 5"
                          : validated && parseInt(capacity, 10) < 3
                          ? "Size must be at least 3"
                          : undefined
                      }
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <CommonTextField
                    label="Unlock Code Length"
                    type="number"
                    placeholder="Enter Unlock Code Length (1 - 10)"
                    value={unlockCode}
                    onChange={(event) => {
                      const val = event.target.value;
                      setUnlockCode(val);
                      settotalnumber(val);
                      setcapacity(val ? String(Number(val) + 1) : "");
                    }}
                    error={
                      Boolean(
                        validated &&
                          (unlockCode === "" ||
                            Number(unlockCode) < 1 ||
                            Number(unlockCode) > 10)
                      )
                    }
                    helperText={
                      validated && Number(unlockCode) < 1
                        ? "Value must be at least 1"
                        : validated && Number(unlockCode) > 10
                        ? "Value must be 10 or less"
                        : validated && unlockCode === ""
                        ? "Please enter unlock code length"
                        : "Sets dynamic code length between 1 and 10"
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider sx={{ my: 2.5, borderColor: colors.dashboard.divider }} />

          {/* Section 3: Schedule & Rollover Settings */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: "rgba(245, 158, 11, 0.1)",
                  color: colors.amber[600],
                }}
              >
                <ScheduleIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: colors.slate[900],
                  fontSize: "16px",
                  letterSpacing: "-0.01em",
                }}
              >
                3. Schedule & Rollover Settings
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <CommonDateTimePicker
                  label="Start Date"
                  value={startTime}
                  onChange={(value) => setstartTime(value)}
                  format="dd-MM-yy h:mma"
                />
              </Grid>

              {/* Rollover / Result Date (MRL & Missile) */}
              {(lotterytype === "2" || lotterytype === "3") && (
                <Grid item xs={12} sm={6}>
                  <CommonDateTimePicker
                    label="Rollover/Result Date"
                    value={endtime}
                    onChange={(value) => {
                      setendtime(value);
                      if (setdrawdate) setdrawdate(value);
                    }}
                    format="dd-MM-yy h:mma"
                  />
                </Grid>
              )}

              {/* Rollover in % (SpinBET, MegaBET, RocketBET) */}
              {lotterytype !== "1" && (
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Rollover in %"
                    type="number"
                    placeholder="0 - 50"
                    value={Rollover}
                    endAdornment="%"
                    onChange={(event) => setRollover(event.target.value)}
                    error={
                      Boolean(
                        validated &&
                          (parseFloat(Rollover) < 0 || parseFloat(Rollover) > 50)
                      )
                    }
                    helperText={
                      validated && parseFloat(Rollover) < 0
                        ? "Cannot be negative"
                        : validated && parseFloat(Rollover) > 50
                        ? "Cannot exceed 50%"
                        : undefined
                    }
                  />
                </Grid>
              )}

              {/* Rollover in Days (MRL & Missile) */}
              {(lotterytype === "2" || lotterytype === "3") && (
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Rollover in Days"
                    type="text"
                    placeholder="e.g. 1"
                    value={Rolloverday}
                    endAdornment="Days"
                    onChange={(event) => {
                      const value = event.target.value;
                      const regex = /^\d*\.?\d*$/;
                      if (regex.test(value) || value === "") {
                        setRolloverdays(value);
                      }
                    }}
                    error={Boolean(validated && parseFloat(Rolloverday) < 1)}
                    helperText={
                      validated && parseFloat(Rolloverday) < 0
                        ? "Cannot be negative"
                        : validated && parseFloat(Rolloverday) < 1
                        ? "Must be at least 1 day"
                        : undefined
                    }
                  />
                </Grid>
              )}

              {/* Partner Share (if partner selected) */}
              {selectPartner !== "" && (
                <Grid item xs={12} sm={6}>
                  <CommonTextField
                    label="Partner's Share in %"
                    type="number"
                    placeholder="0 - 100"
                    value={partnerPercent}
                    endAdornment="%"
                    onChange={(event) => setPartnerPercent(event.target.value)}
                    error={
                      Boolean(
                        validated &&
                          (parseFloat(partnerPercent) < 0 ||
                            parseFloat(partnerPercent) > 100)
                      )
                    }
                    helperText={
                      validated && parseFloat(partnerPercent) < 0
                        ? "Cannot be negative"
                        : validated && parseFloat(partnerPercent) > 100
                        ? "Must be 0 - 100%"
                        : undefined
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Section 4: Cost Calculation Breakdown & Deployment Action */}
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: borderRadiusPx.xl,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.dashboard.cardBorder}`,
              boxShadow: customShadows.cardFlat,
              mt: 2.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: borderRadiusPx.lg,
                    bgcolor: colors.blue[50],
                    color: colors.primary.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CostIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: colors.slate[500],
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Total Required Initial Deposit
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "22px", sm: "26px" },
                      fontWeight: 900,
                      color: colors.slate[900],
                      lineHeight: 1.15,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {standardCreationFeeTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })} {currencySymbol}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                <Typography sx={{ fontSize: "12.5px", color: colors.slate[600] }}>
                  Winner Prize ({totalPrize || "0"} {currencySymbol}) + {lotteryCreateFee || "0"}% Protocol Creation Fee
                </Typography>
                {lotterytype === "2" && minPlayers > 0 && (
                  <Typography sx={{ fontSize: "12px", color: colors.emerald[700], fontWeight: 600, mt: 0.25 }}>
                    Minimum players for draw: <strong>{minPlayers}</strong>
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Prize bounds warning */}
            {totalPrize !== "" && hasPrizeBounds && !isStandardPrizeInBounds && (
              <Alert
                severity="error"
                icon={<InfoIcon />}
                sx={{
                  mb: 2,
                  py: 0.5,
                  borderRadius: borderRadiusPx.md,
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                Invalid winning prize amount. Allowed range:{" "}
                <strong>
                  {safeFromWei(approved?.minPrize)} - {safeFromWei(approved?.maxPrize)} {currencySymbol}
                </strong>
              </Alert>
            )}

            {/* Submit Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
              <CommonButton
                type="submit"
                variant="contained"
                size="large"
                loading={loading}
                disabled={loading || (totalPrize !== "" && hasPrizeBounds && !isStandardPrizeInBounds)}
                sx={{
                  minWidth: { xs: "100%", sm: "260px" },
                  py: 1.35,
                  fontWeight: 800,
                  fontSize: "15px",
                  letterSpacing: "0.02em",
                  boxShadow: customShadows.primaryGlow,
                }}
              >
                Create Lottery
              </CommonButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

CreateLotteryForm.propTypes = {
  lotterytype: PropTypes.string.isRequired,
  setlotterytype: PropTypes.func.isRequired,
  partnerlist: PropTypes.array,
  selectPartner: PropTypes.string,
  setSelectPartner: PropTypes.func,
  entryfee: PropTypes.string,
  setentryfee: PropTypes.func,
  totalPrize: PropTypes.string,
  settotalPrize: PropTypes.func,
  totalnumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  settotalnumber: PropTypes.func,
  capacity: PropTypes.string,
  setcapacity: PropTypes.func,
  unlockCode: PropTypes.string,
  setUnlockCode: PropTypes.func,
  startTime: PropTypes.instanceOf(Date),
  setstartTime: PropTypes.func,
  endtime: PropTypes.instanceOf(Date),
  setendtime: PropTypes.func,
  setdrawdate: PropTypes.func,
  Rollover: PropTypes.string,
  setRollover: PropTypes.func,
  Rolloverday: PropTypes.string,
  setRolloverdays: PropTypes.func,
  partnerPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  setPartnerPercent: PropTypes.func,
  minPlayers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setminPlayers: PropTypes.func,
  lotteryCreateFee: PropTypes.string,
  approved: PropTypes.object,
  validated: PropTypes.bool,
  loading: PropTypes.bool,
  currencySymbol: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,

  machineName: PropTypes.string,
  setMachineName: PropTypes.func,
  slotCategory: PropTypes.string,
  setSlotCategory: PropTypes.func,
  slotSymbols: PropTypes.array,
  setSlotSymbols: PropTypes.func,
  onCategoryChange: PropTypes.func,
  theme: PropTypes.string,
  setTheme: PropTypes.func,
  currency: PropTypes.string,
  setCurrency: PropTypes.func,
  betAmount: PropTypes.string,
  setBetAmount: PropTypes.func,
  seedJackpot: PropTypes.string,
  setSeedJackpot: PropTypes.func,
  progressivePercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setProgressivePercent: PropTypes.func,
  miniPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setMiniPercent: PropTypes.func,
  majorPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setMajorPercent: PropTypes.func,
  megaPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setMegaPercent: PropTypes.func,
  maxSpins: PropTypes.string,
  setMaxSpins: PropTypes.func,
  slotStartTime: PropTypes.instanceOf(Date),
  setSlotStartTime: PropTypes.func,
  slotEndTime: PropTypes.instanceOf(Date),
  setSlotEndTime: PropTypes.func,
  slotPartner: PropTypes.string,
  setSlotPartner: PropTypes.func,
  slotPartnerPercent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSlotPartnerPercent: PropTypes.func,
};

export default CreateLotteryForm;
